import { createApi } from '@reduxjs/toolkit/query/react';

import fetchBaseQuery from '../fetchBaseQuery';
import type {
    DownloadAvailabilityRequest,
    GetLanguagesResponse,
    RemoveEntryRequest,
    RemoveEntryResponse,
    SearchRequest,
    SearchResponse,
} from './types';

export * from './types';

const translationMemorySearchTag = 'TRANSLATION_MEMORY_SEARCH' as const;

const translationMemoryApi = createApi({
    reducerPath: 'lokaliseInternal_translationMemory',
    baseQuery: fetchBaseQuery(),
    tagTypes: [translationMemorySearchTag],
    endpoints: (builder) => ({
        getLanguages: builder.query<GetLanguagesResponse, { fileId: string }>({
            query: ({ fileId }) => ({
                method: 'GET',
                url: `/translation_memory/${fileId}/languages`,
            }),
        }),
        downloadAvailability: builder.query<void, DownloadAvailabilityRequest>({
            query: ({ teamId, fileId, sourceLanguageId, targetLanguageId }) => ({
                method: 'GET',
                url: `/translation_memory/${teamId}/files/${fileId}/download-availability?source=${sourceLanguageId}&target=${targetLanguageId}`,
            }),
        }),
        search: builder.query<SearchResponse, SearchRequest>({
            query: ({
                fromLang,
                toLang,
                exactMatch,
                includeRegions,
                phrase,
                projectId,
                myEntries,
                resultsLimit = 50,
                searchTarget,
                caseSensitive,
            }) => ({
                method: 'GET',
                url: '/translation_memory/search',
                params: {
                    /* eslint-disable @typescript-eslint/naming-convention */
                    from_lang: fromLang,
                    to_lang: toLang,
                    exact_match: exactMatch,
                    case_sensitive: caseSensitive,
                    include_regions: includeRegions,
                    phrase,
                    project_id: projectId,
                    my_entries: myEntries,
                    results_limit: resultsLimit,
                    search_target: searchTarget,
                    /* eslint-enable @typescript-eslint/naming-convention */
                },
            }),
            providesTags: [{ type: translationMemorySearchTag, id: 'RESULT_LIST' }],
        }),
        removeEntry: builder.mutation<RemoveEntryResponse, RemoveEntryRequest>({
            query: ({ translationMemoryEntryId }) => ({
                method: 'DELETE',
                url: '/translation_memory',
                params: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    tm_id: translationMemoryEntryId,
                },
            }),
            invalidatesTags: [{ type: translationMemorySearchTag, id: 'RESULT_LIST' }],
        }),
    }),
});

export default translationMemoryApi;

export const {
    useGetLanguagesQuery,
    useDownloadAvailabilityQuery,
    useLazyDownloadAvailabilityQuery,
    useSearchQuery,
    useLazySearchQuery,
    useRemoveEntryMutation,
} = translationMemoryApi;
